import React, { useState, useContext, useEffect, useCallback } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { UIContext } from "../layout"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import externalLinks from "../../../external-links"
import FormInput from "./form-input"
import useFormSubmit from "../hooks/useHubspotFormSubmission"

import {
  FormGrid,
  FormSubmitButton,
  ValidationContainer,
  ErrorBlock,
  SuccessBlock,
} from "./form.style"

const HUBSPOT_ENDPOINT = externalLinks.hubspot.partnerFormSubmissionApi

const isDevelopment = process.env.NODE_ENV === "development"

const ContactForm = ({ className }) => {
  const query = useStaticQuery(graphql`
    query {
      successIcon: file(
        relativePath: { eq: "0_Global_Assets/icon_success.svg" }
      ) {
        publicURL
      }
      failIcon: file(relativePath: { eq: "0_Global_Assets/icon_failure.svg" }) {
        publicURL
      }
    }
  `)

  const {
    state: { clientLoaded },
  } = useContext(UIContext)

  const submitForm = useFormSubmit(HUBSPOT_ENDPOINT)

  const { register, errors, handleSubmit } = useForm({
    defaultValues: {
      primary_vertical_target_market: "DEFAULT",
      primary_market_served: "DEFAULT",
      active_opportunity: "DEFAULT",
    },
  })

  const [showCaptcha, setShowCaptcha] = useState(false)
  const [isCaptchaValidated, setIsCaptchaValidated] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [submissionFailure, setSubmissionFailure] = useState(false)

  const isButtonDisabled = useCallback(() => {
    //button disabled if we have validation errors, have submitted the form, or captcha has not been confirmed in production
    return (
      Object.keys(errors).length > 0 ||
      hasSubmitted ||
      (isDevelopment ? false : !isCaptchaValidated)
    )
  }, [isCaptchaValidated, errors, hasSubmitted])

  const onSubmitCallback = (formData, e) => {
    e.preventDefault()

    submitForm(formData)
      .then(data => {
        if (!data.errors) {
          setSubmissionFailure(false)
          setHasSubmitted(true)
        } else {
          setSubmissionFailure(true)
        }
      })
      .catch(error => {
        setSubmissionFailure(true)
      })
  }

  //I think we did this to stop the captcha loading from blocking page load / affecting performance scores?
  useEffect(() => {
    if (clientLoaded && !showCaptcha) {
      setTimeout(() => {
        setShowCaptcha(true)
      }, 10000)
    }
  }, [clientLoaded, showCaptcha])

  return (
    <div className={className}>
      {hasSubmitted && (
        <SuccessBlock>
          <img
            alt="Form Submission Success Icon"
            src={query.successIcon.publicURL}
          />
          <p>Thank you! Your message has been successfully sent.</p>
        </SuccessBlock>
      )}
      {Object.keys(errors).length > 0 && !hasSubmitted && (
        <ErrorBlock>
          <img
            alt="Form Submission Failure Icon"
            src={query.failIcon.publicURL}
          />
          <p>One or more fields are missing required information.</p>
        </ErrorBlock>
      )}
      {submissionFailure && (
        <ErrorBlock>
          <p>
            An issue occurred sending the message, please get in touch with us
            directly at{" "}
            <a href="mailto:contact@mappedin.com">contact@mappedin.com</a>
          </p>
        </ErrorBlock>
      )}

      <FormGrid onSubmit={handleSubmit(onSubmitCallback)}>
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="firstname"
          id="form-fname"
          inputTitle="First Name"
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="lastname"
          id="form-lname"
          inputTitle="Last Name"
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="email"
          id="form-email"
          inputTitle="E-Mail"
          handler={register({
            required: "This field is required (*)",
            //email regex from https://emailregex.com/
            //eslint-disable-next-line
            pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: "Enter a valid email address",
          })}
          error={
            errors.email?.type === "pattern"
              ? "Enter a valid email address"
              : errors.email?.type === "required"
              ? errors.email.message
              : ""
          }
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="phone"
          id="form-phone"
          inputTitle="Phone Number"
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="jobtitle"
          id="form-jobtitle"
          inputTitle="Job Title"
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="company"
          id="form-company"
          inputTitle="Company Name"
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="primary_vertical_target_market"
          id="form-vertical"
          inputTitle="What is your primary target vertical?"
          as={FormInput.Select}
          colSpan={2}
          handler={register({
            validate: value =>
              value === "DEFAULT" ? "This field is required (*)" : null,
          })}
        >
          <option disabled value="DEFAULT">
            Please Select
          </option>

          <option value="Grocery">Grocery</option>
          <option value="Office Buildings">Office Buildings</option>
          <option value="Retail">Retail</option>
          <option value="Shopping Malls">Shopping Malls</option>
          <option value="Smart Cities">Smart Cities</option>
          <option value="Sports & Entertainment">Sports & Entertainment</option>
          <option value="Transportation">Transportation</option>
          <option value="Warehouses">Warehouses</option>
          <option value="Other">Other</option>
        </FormInput>
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="primary_market_served"
          id="form-market"
          inputTitle="What is your primary market served?"
          as={FormInput.Select}
          colSpan={2}
          handler={register({
            validate: value =>
              value === "DEFAULT" ? "This field is required (*)" : null,
          })}
        >
          <option disabled value="DEFAULT">
            Please Select
          </option>
          <option value="The Americas">Americas</option>
          <option value="Latin America">Latin America</option>
          <option value="Asia Pacific">Asia Pacific</option>
          <option value="Europe, Middle East, Africa">
            Europe, Middle East, Africa
          </option>
        </FormInput>
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="primary_product_sold"
          id="form-product"
          inputTitle="What is your primary product sold?"
          placeholder="Hardware, software, services, etc."
          colSpan={2}
          handler={register({ required: "This field is required (*)" })}
        />
        <FormInput
          disabled={hasSubmitted}
          errors={errors}
          name="active_opportunity"
          id="form-opportunity"
          inputTitle="Do you have an active opportunity?"
          as={FormInput.Select}
          colSpan={2}
          handler={register({
            validate: value =>
              value === "DEFAULT" ? "This field is required (*)" : null,
          })}
        >
          <option disabled value="DEFAULT">
            Please Select
          </option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </FormInput>
        <ValidationContainer>
          {showCaptcha && (
            <ReCAPTCHA
              sitekey="6LcYAakZAAAAAEfW6OEAv4QJNAs9Zg50ccHhSIjL"
              onChange={() => setIsCaptchaValidated(true)}
              onExpired={() => setIsCaptchaValidated(false)}
            />
          )}
        </ValidationContainer>
        <FormSubmitButton type="submit" disabled={isButtonDisabled()}>
          <span>Start The Conversation</span>
        </FormSubmitButton>
      </FormGrid>
    </div>
  )
}

export default ContactForm
